import Dexie, { type Table } from 'dexie';

// Sync status for offline-first operations
export type SyncStatus = 'synced' | 'pending' | 'conflict';

// Base interface for all entities with sync metadata
export interface SyncableEntity {
  syncStatus: SyncStatus;
  lastSyncAt?: number;
  localUpdatedAt: number;
  serverUpdatedAt?: number;
}

// KeyValue pair used for headers, params, form data
export interface KeyValueItem {
  key: string;
  value: string;
  description?: string;
  enabled: boolean;
}

// Request body configuration
export interface RequestBody {
  type: 'none' | 'raw' | 'form-data' | 'x-www-form-urlencoded' | 'binary';
  raw?: { content: string; contentType: 'json' | 'xml' | 'text' | 'html' | 'javascript' };
  formData?: KeyValueItem[];
  urlencoded?: KeyValueItem[];
}

// Request authentication configuration
export interface RequestAuth {
  type: 'none' | 'inherit' | 'bearer' | 'basic' | 'api-key' | 'oauth2' | 'hawk';
  bearer?: { token: string };
  basic?: { username: string; password: string };
  apiKey?: { key: string; value: string; addTo: 'header' | 'query' };
  oauth2?: {
    grantType: 'client_credentials' | 'password' | 'authorization_code';
    accessTokenUrl: string;
    clientId: string;
    clientSecret?: string;
    scope?: string;
    username?: string;
    password?: string;
    accessToken?: string;
    tokenExpiresAt?: number;
  };
  hawk?: {
    authId: string;
    authKey: string;
    algorithm: 'sha256' | 'sha1';
    ext?: string;
    includePayloadHash: boolean;
  };
}

// Collection auth configuration (mirrors request auth)
export interface CollectionAuth {
  type: 'none' | 'bearer' | 'basic' | 'api-key' | 'oauth2' | 'hawk';
  bearer?: { token: string };
  basic?: { username: string; password: string };
  apiKey?: { key: string; value: string; addTo: 'header' | 'query' };
  oauth2?: {
    grantType: 'client_credentials' | 'password' | 'authorization_code';
    accessTokenUrl: string;
    clientId: string;
    clientSecret?: string;
    scope?: string;
    username?: string;
    password?: string;
    accessToken?: string;
    tokenExpiresAt?: number;
  };
  hawk?: {
    authId: string;
    authKey: string;
    algorithm: 'sha256' | 'sha1';
    ext?: string;
    includePayloadHash: boolean;
  };
}

// Collection stored in IndexedDB
export interface DBCollection extends SyncableEntity {
  id: string;
  name: string;
  description?: string;
  workspaceId: string;
  variables?: DBVariable[];
  auth?: CollectionAuth;
  createdAt: number;
  updatedAt: number;
}

// Folder within a collection
export interface DBFolder extends SyncableEntity {
  id: string;
  name: string;
  collectionId: string;
  parentId?: string; // null for root-level folders
  order: number;
  createdAt: number;
  updatedAt: number;
}

// Full request stored in IndexedDB
export interface DBRequest extends SyncableEntity {
  id: string;
  name: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
  url: string;
  headers: KeyValueItem[];
  params: KeyValueItem[];
  body?: RequestBody;
  auth?: RequestAuth;
  collectionId: string;
  folderId?: string; // null for requests at collection root
  order: number;
  createdAt: number;
  updatedAt: number;
}

// Environment variables
export interface DBVariable {
  key: string;
  value: string;
  isSecret: boolean;
  enabled: boolean;
}

// Environment stored in IndexedDB
export interface DBEnvironment extends SyncableEntity {
  id: string;
  name: string;
  workspaceId: string;
  variables: DBVariable[];
  isGlobal: boolean;
  createdAt: number;
  updatedAt: number;
}

// History entry for executed requests
export interface DBHistoryEntry {
  id: string;
  requestId?: string; // Link to saved request if applicable
  workspaceId: string;

  // Request snapshot at time of execution
  request: {
    name: string;
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
    url: string;
    headers: KeyValueItem[];
    params: KeyValueItem[];
    body?: RequestBody;
    auth?: RequestAuth;
  };

  // Response data
  response?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
    size: number;
    time: number; // Response time in ms
  };

  // Error if request failed
  error?: {
    message: string;
    code?: string;
  };

  executedAt: number;
  environmentId?: string; // Environment used for variable interpolation
}

// Pending sync operations queue
export interface DBSyncQueueItem {
  id?: number; // Auto-incremented
  entityType: 'collection' | 'folder' | 'request' | 'environment';
  entityId: string;
  operation: 'create' | 'update' | 'delete';
  payload?: unknown;
  createdAt: number;
  retryCount: number;
  lastError?: string;
}

// Dexie database class
export class SnailmanDB extends Dexie {
  collections!: Table<DBCollection, string>;
  folders!: Table<DBFolder, string>;
  requests!: Table<DBRequest, string>;
  environments!: Table<DBEnvironment, string>;
  history!: Table<DBHistoryEntry, string>;
  syncQueue!: Table<DBSyncQueueItem, number>;

  constructor() {
    super('SnailmanDB');

    this.version(1).stores({
      // Collections: indexed by id, workspaceId for filtering
      collections: 'id, workspaceId, syncStatus',

      // Folders: indexed by id, collectionId for listing, parentId for tree
      folders: 'id, collectionId, parentId, syncStatus',

      // Requests: indexed by id, collectionId and folderId for listing
      requests: 'id, collectionId, folderId, syncStatus',

      // Environments: indexed by id, workspaceId for filtering
      environments: 'id, workspaceId, isGlobal, syncStatus',

      // History: indexed by id, workspaceId and executedAt for chronological listing
      history: 'id, workspaceId, requestId, executedAt',

      // Sync queue: auto-increment id, entityType and operation for processing
      syncQueue: '++id, entityType, entityId, operation'
    });
  }

  // Helper method to mark entity as pending sync
  async markPendingSync<T extends SyncableEntity>(
    table: Table<T, string>,
    id: string
  ): Promise<void> {
    await table.update(id, {
      syncStatus: 'pending',
      localUpdatedAt: Date.now()
    } as Partial<T>);
  }

  // Helper method to add item to sync queue
  async queueSyncOperation(
    entityType: DBSyncQueueItem['entityType'],
    entityId: string,
    operation: DBSyncQueueItem['operation'],
    payload?: unknown
  ): Promise<void> {
    await this.syncQueue.add({
      entityType,
      entityId,
      operation,
      payload,
      createdAt: Date.now(),
      retryCount: 0
    });
  }

  // Get all pending items from sync queue
  async getPendingSyncItems(): Promise<DBSyncQueueItem[]> {
    return this.syncQueue.toArray();
  }

  // Remove item from sync queue after successful sync
  async removeSyncQueueItem(id: number): Promise<void> {
    await this.syncQueue.delete(id);
  }

  // Clear all history entries for a workspace
  async clearHistory(workspaceId: string): Promise<void> {
    await this.history.where('workspaceId').equals(workspaceId).delete();
  }

  // Get recent history entries
  async getRecentHistory(
    workspaceId: string,
    limit: number = 50
  ): Promise<DBHistoryEntry[]> {
    return this.history
      .where('workspaceId')
      .equals(workspaceId)
      .reverse()
      .sortBy('executedAt')
      .then(entries => entries.slice(0, limit));
  }
}

// Create and export singleton database instance
export const db = new SnailmanDB();

// Export type for use with dexie-react-hooks
export type { Table };
